import React, { useState, useEffect } from 'react';
import './OfficerPhaseProgression.css';
import PhaseResourceViewer from './PhaseResourceViewer';
import { programPhases } from '../data/programPhases';

export function OfficerPhaseProgression({ userId }) {
  const [phases, setPhases] = useState(programPhases);
  const [progress, setProgress] = useState({});
  const [loading, setLoading] = useState(false);
  const [expandedPhase, setExpandedPhase] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadProgress();
  }, [userId]);

  const loadProgress = async () => {
    try {
      if (userId) {
        const response = await fetch(`/api/training/phase-progress?userId=${userId}`);
        if (response.ok) {
          const data = await response.json();
          const progressMap = {};
          data.forEach(item => {
            progressMap[item.phase_id] = item;
          });
          setProgress(progressMap);
        }
      }
      setLoading(false);
    } catch (err) {
      console.error('Error loading progress:', err);
      setLoading(false);
    }
  };

  const updatePhaseProgress = async (phaseId, newStatus, newProgress = 0) => {
    try {
      const response = await fetch('/api/training/phase-progress', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userId,
          phaseId,
          status: newStatus,
          progressPercentage: newProgress
        })
      });

      if (!response.ok) throw new Error('Failed to update progress');
      const updated = await response.json();
      setProgress({
        ...progress,
        [phaseId]: updated
      });
    } catch (err) {
      console.error('Error updating progress:', err);
    }
  };

  if (loading) {
    return <div className="phase-loading">Loading program phases...</div>;
  }

  if (error) {
    return <div className="phase-error">Error: {error}</div>;
  }

  return (
    <div className="officer-phase-container">
      <div className="phase-header">
        <h1>🏢 Building Your Compliance & Risk Program</h1>
        <p>A structured 5-phase approach to establishing an effective program</p>
      </div>

      <div className="phases-timeline">
        {phases.map((phase, index) => {
          const phaseProgress = progress[phase.id] || { status: 'not-started', progress_percentage: 0 };
          const isLocked = index > 0 && (!progress[phases[index - 1].id] || progress[phases[index - 1].id].status !== 'completed');
          const isExpanded = expandedPhase === phase.id;

          return (
            <div
              key={phase.id}
              className={`phase-card ${phaseProgress.status} ${isLocked ? 'locked' : ''} ${isExpanded ? 'expanded' : ''}`}
            >
              <div
                className="phase-card-header"
                onClick={() => !isLocked && setExpandedPhase(isExpanded ? null : phase.id)}
              >
                <div className="phase-indicator">
                  <span className="phase-number">{phase.order_index}</span>
                  <span className="phase-status-icon">
                    {isLocked ? '🔒' : phaseProgress.status === 'completed' ? '✓' : '○'}
                  </span>
                </div>

                <div className="phase-title-section">
                  <h2>{phase.name}</h2>
                  <p className="phase-duration">
                    {phase.duration_weeks} weeks • {phaseProgress.progress_percentage}% complete
                  </p>
                </div>

                <div className="phase-arrow">
                  {!isLocked && (isExpanded ? '▼' : '▶')}
                </div>
              </div>

              {isExpanded && !isLocked && (
                <div className="phase-details">
                  <div className="phase-resources-section">
                    <PhaseResourceViewer phase={phase} />
                  </div>

                  <div className="phase-progress-section">
                    <h3>Your Progress</h3>
                    <div className="progress-bar-container">
                      <div
                        className="progress-bar"
                        style={{ width: `${phaseProgress.progress_percentage}%` }}
                      ></div>
                    </div>
                    <p>{phaseProgress.progress_percentage}% complete</p>

                    {phaseProgress.status === 'not-started' && (
                      <button
                        onClick={() => updatePhaseProgress(phase.id, 'in-progress', 25)}
                        className="phase-button start"
                      >
                        Start Phase
                      </button>
                    )}

                    {phaseProgress.status === 'in-progress' && (
                      <div className="progress-controls">
                        <input
                          type="range"
                          min="0"
                          max="100"
                          value={phaseProgress.progress_percentage}
                          onChange={(e) => updatePhaseProgress(phase.id, 'in-progress', parseInt(e.target.value))}
                          className="progress-slider"
                        />
                        <button
                          onClick={() => updatePhaseProgress(phase.id, 'completed', 100)}
                          className="phase-button complete"
                        >
                          Mark Complete
                        </button>
                      </div>
                    )}

                    {phaseProgress.status === 'completed' && (
                      <div className="phase-completed-badge">
                        ✓ Phase Completed!
                      </div>
                    )}
                  </div>
                </div>
              )}

              {phaseProgress.status === 'completed' && (
                <div className="phase-completed-indicator">
                  <span>✓ Completed</span>
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className="phase-footer">
        <p>💡 Complete phases sequentially to build a strong foundation for your program.</p>
      </div>
    </div>
  );
}

export default OfficerPhaseProgression;
